import { collection, doc, query, where, getDocs, writeBatch, runTransaction, serverTimestamp } from '../../lib/auditedFirestore';
import { db } from '../../lib/firebase';
import { buildQuestionEntry } from './practiceModel';
import { validQuestionNumber } from './voiceCommands';
import { prepareQuestionForSave } from './questionContentStorage';
import { scenarioValidation } from './scenarioQuestions';

// Allocate once per editing session so retrying a failed/unknown commit is idempotent.
export function scenarioIdentity(existing) {
    const createId = () => doc(collection(db, 'practiceQuestions')).id;
    if (existing) {
        if (!existing.id || existing.subquestionIds?.length !== 5 || new Set(existing.subquestionIds).size !== 5 || existing.subquestionIds.includes(existing.id)) throw new Error('This scenario has invalid requirement links.');
        return { id: existing.id, partIds: [...existing.subquestionIds] };
    }
    return { id: createId(), partIds: Array.from({ length: 5 }, createId) };
}

export async function saveScenario(draft, paper, identity, onStatus = () => {}) {
    const problem = scenarioValidation(draft);
    if (problem) throw new Error(problem.message);
    if (!identity?.id || identity.partIds?.length !== 5 || new Set([identity.id, ...identity.partIds]).size !== 6) throw new Error('Invalid scenario identity.');
    onStatus('Checking question numbers…');
    const existing = await getDocs(query(collection(db, 'practiceQuestions'), where('paperId', '==', paper.id)));
    const ownIds = new Set([identity.id, ...identity.partIds]);
    const numbers = new Set(draft.parts.map(part => validQuestionNumber(part.questionNumber)).filter(Boolean));
    if (existing.docs.some(item => !ownIds.has(item.id) && numbers.has(validQuestionNumber(item.data().questionNumber)))) throw new Error('One of these question numbers is already assigned in this subject. Choose another number.');
    const previous = new Map(existing.docs.map(item => [item.id, item.data()]));
    if (previous.has(identity.id) && previous.get(identity.id).recordKind !== 'scenario') throw new Error('The scenario link is invalid. Reopen it from Manage Questions.');
    if (identity.partIds.some(id => previous.has(id) && previous.get(id).scenarioId !== identity.id)) throw new Error('A requirement belongs to a different scenario. Reopen this set before saving.');
    const timestamps = id => ({ createdAt: previous.get(id)?.createdAt || serverTimestamp(), updatedAt: serverTimestamp() });
    const video = { bunnyVideoId: String(draft.bunnyVideoId || ''), bunnyLibraryId: draft.bunnyVideoId ? String(draft.bunnyLibraryId || '') : '' };
    const shared = { recordKind: 'scenario', type: 'scenario', section: 'B', title: draft.title.trim(), topic: draft.topic.trim(), text: draft.text || '', images: draft.images || [],
        paperId: paper.id, paperName: paper.name || '', difficulty: draft.difficulty || 'standard', ...video,
        marks: Number(draft.parts.reduce((sum, part) => sum + Number(part.marks), 0).toFixed(2)), subquestionIds: identity.partIds, ...timestamps(identity.id) };
    const entries = [[identity.id, shared], ...draft.parts.map((part, index) => {
        // Runtime joined context is deliberately not persisted in the five answer documents.
        const { scenario, practiceNumber, ...source } = part;
        return [identity.partIds[index], { ...buildQuestionEntry(source, { paper, topic: draft.topic, bunnyVideoId: part.bunnyVideoId, bunnyLibraryId: part.bunnyLibraryId }),
            section: 'B', scenarioId: identity.id, scenarioOrder: index, marks: Number(part.marks), difficulty: draft.difficulty || 'standard',
            questionNumber: validQuestionNumber(part.questionNumber) || '', ...timestamps(identity.partIds[index]) }];
    })];
    const prepared = [];
    for (const [id, entry] of entries) prepared.push([id, await prepareQuestionForSave(entry, { onStatus })]);
    onStatus('Saving the scenario and all five requirements…');
    const batch = writeBatch(db);
    prepared.forEach(([id, data]) => batch.set(doc(db, 'practiceQuestions', id), data));
    await batch.commit();
    return identity.id;
}

export async function deleteScenario(scenario) {
    const identity = scenarioIdentity(scenario);
    await runTransaction(db, async transaction => {
        const records = await Promise.all([identity.id, ...identity.partIds].map(id => transaction.get(doc(db, 'practiceQuestions', id))));
        records.filter(record => record.exists()).forEach(record => transaction.delete(record.ref));
    });
    return [identity.id, ...identity.partIds];
}
